<div class="page-container">
  <!-- Main Content -->
  <main class="page-main">
    <div class="page-content full-height" [class.is-condensed]="isCondensed">
      <!-- Header Section -->
      <div class="page-title-container">
        <app-breadcrumb></app-breadcrumb>
        <h1 class="page-title">
          {{
            viewMode === "requests"
              ? "Time Off Requests"
              : "Resources Directory"
          }}
        </h1>
        <div class="page-title-actions">
          <!-- Hidden rather than disabled outside the calendar: there is no
               calendar to print, and .inline-action.is-disabled sets
               pointer-events: none, so an explanatory tooltip on a disabled
               control could never be read. Matches the shared time-off
               calendar, which also hides its print button. -->
          @if (viewMode === "calendar") {
            <div
              class="inline-action print-action"
              role="button"
              tabindex="0"
              aria-label="Print calendar"
              matTooltip="Print calendar"
              (click)="printCalendar()"
              (keydown.enter)="printCalendar()"
              (keydown.space)="$event.preventDefault(); printCalendar()"
            >
              <mat-icon class="inline-action-icon">print</mat-icon>
              <span>Print</span>
            </div>
          }
          <div
            class="inline-action export-action"
            [class.is-disabled]="isExporting"
            role="button"
            [attr.tabindex]="isExporting ? -1 : 0"
            [attr.aria-disabled]="isExporting"
            (click)="openExportConfig()"
            (keydown.enter)="openExportConfig()"
            (keydown.space)="$event.preventDefault(); openExportConfig()"
            matTooltip="Configure and export to Excel"
          >
            @if (isExporting) {
              <mat-spinner diameter="18"></mat-spinner>
            } @else {
              <mat-icon class="inline-action-icon">download</mat-icon>
            }
            <span>Export</span>
          </div>
          <div
            #columnsOrigin="cdkOverlayOrigin"
            cdkOverlayOrigin
            class="inline-action columns-toggle"
            [class.is-disabled]="viewMode !== 'table'"
            role="button"
            [attr.tabindex]="viewMode === 'table' ? 0 : -1"
            [attr.aria-disabled]="viewMode !== 'table'"
            [attr.aria-expanded]="viewMode === 'table' ? showColumnsMenu : null"
            aria-label="Show or hide columns"
            [matTooltip]="
              viewMode === 'table'
                ? 'Show or hide columns'
                : 'Column options are only available in the resource list'
            "
            (click)="toggleColumnsMenu($event)"
            (keydown.enter)="toggleColumnsMenu($event)"
            (keydown.space)="$event.preventDefault(); toggleColumnsMenu($event)"
          >
            <mat-icon class="inline-action-icon">view_column</mat-icon>
            <span>Columns</span>
          </div>
          @if (viewMode === "table") {
            <ng-template
              cdkConnectedOverlay
              [cdkConnectedOverlayOrigin]="columnsOrigin"
              [cdkConnectedOverlayOpen]="showColumnsMenu"
              [cdkConnectedOverlayPositions]="exportOverlayPositions"
              [cdkConnectedOverlayHasBackdrop]="true"
              cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
              (backdropClick)="showColumnsMenu = false"
            >
              <div
                class="split-action-menu columns-menu"
                role="menu"
                aria-label="Show, hide, or reorder columns"
                cdkDropList
                (cdkDropListDropped)="onColumnDrop($event)"
              >
                @for (column of columnOrder; track column) {
                  <button
                    type="button"
                    class="split-action-menu-item columns-menu-item"
                    cdkDrag
                    cdkDragLockAxis="y"
                    cdkDragBoundary=".columns-menu"
                    cdkDragPreviewContainer="parent"
                    role="menuitemcheckbox"
                    [attr.aria-checked]="isColumnVisible(column)"
                    [attr.aria-disabled]="!isColumnHideable(column)"
                    [class.is-selected]="isColumnVisible(column)"
                    [class.is-locked]="!isColumnHideable(column)"
                    [matTooltip]="
                      isColumnHideable(column)
                        ? ''
                        : 'This column is always shown'
                    "
                    (click)="toggleColumn(column)"
                  >
                    <mat-icon
                      class="columns-menu-drag-handle"
                      cdkDragHandle
                      aria-label="Drag to reorder"
                      (click)="$event.stopPropagation()"
                      >drag_indicator</mat-icon
                    >
                    <span class="split-action-menu-item-info">
                      <span class="split-action-menu-item-title">{{
                        getColumnLabel(column)
                      }}</span>
                    </span>
                    @if (isColumnVisible(column)) {
                      <mat-icon class="split-action-menu-item-check"
                        >check</mat-icon
                      >
                    }
                  </button>
                }
                <button
                  type="button"
                  class="split-action-menu-item columns-menu-reset"
                  [disabled]="isColumnConfigDefault()"
                  (click)="resetColumns()"
                >
                  <mat-icon class="columns-menu-reset-icon">replay</mat-icon>
                  <span class="split-action-menu-item-info">
                    <span class="split-action-menu-item-title"
                      >Reset to default</span
                    >
                  </span>
                </button>
              </div>
            </ng-template>
          }
          <div
            class="inline-action condense-toggle"
            [class.is-disabled]="viewMode === 'calendar'"
            role="button"
            [attr.tabindex]="viewMode === 'calendar' ? -1 : 0"
            [attr.aria-disabled]="viewMode === 'calendar'"
            [attr.aria-pressed]="viewMode === 'calendar' ? null : isCondensed"
            [matTooltip]="
              viewMode === 'calendar'
                ? 'Row spacing options are not available in calendar view'
                : isCondensed
                  ? 'Switch to standard rows'
                  : 'Switch to condensed rows'
            "
            (click)="toggleCondensedRows()"
            (keydown.enter)="toggleCondensedRows()"
            (keydown.space)="$event.preventDefault(); toggleCondensedRows()"
          >
            <mat-icon class="inline-action-icon">{{
              isCondensed ? "expand_all" : "collapse_all"
            }}</mat-icon>
          </div>
          <mat-button-toggle-group
            [value]="viewMode"
            (change)="onViewModeChange($event.value)"
            class="view-toggle"
          >
            <mat-button-toggle value="table" matTooltip="Resource list">
              <mat-icon>groups</mat-icon>
            </mat-button-toggle>
            <mat-button-toggle value="calendar" matTooltip="Time off calendar">
              <mat-icon>calendar_month</mat-icon>
            </mat-button-toggle>
            @if (canApproveTimeOff) {
              <mat-button-toggle
                value="requests"
                matTooltip="Time off requests"
              >
                <mat-icon>pending_actions</mat-icon>
              </mat-button-toggle>
            }
          </mat-button-toggle-group>
        </div>
      </div>

      <!-- Filters Section -->
      @if (viewMode === "requests") {
        <mat-card class="filters-card">
          <mat-card-content>
            <div class="main-search-row">
              <!-- Search Field -->
              <mat-form-field
                appearance="outline"
                class="filter-field search-field"
              >
                <mat-label>Search resources by name</mat-label>
                <input
                  matInput
                  [(ngModel)]="requestsSearchQuery"
                  placeholder="Search resources by name"
                  autocomplete="off"
                  (input)="onRequestsSearchInput()"
                />
                <mat-icon
                  matSuffix
                  [style.cursor]="requestsSearchQuery ? 'pointer' : 'default'"
                  (click)="clearRequestsSearch()"
                  >{{ requestsSearchQuery ? "close" : "search" }}</mat-icon
                >
              </mat-form-field>

              <!-- Practice Areas Filter -->
              <mat-form-field
                appearance="outline"
                class="filter-field practice-areas-field"
              >
                <mat-label>Practice Areas</mat-label>
                <mat-select
                  [(value)]="requestsPracticeAreaFilter"
                  multiple
                  (selectionChange)="onRequestsPracticeAreaChange($event.value)"
                >
                  @for (pa of practiceAreas; track pa.practiceCode) {
                    <mat-option [value]="pa.practiceCode">
                      {{ pa.description }}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>

              <!-- Office Filter -->
              <mat-form-field
                appearance="outline"
                class="filter-field offices-field"
              >
                <mat-label>Offices</mat-label>
                <mat-select
                  [(value)]="requestsOfficeFilter"
                  multiple
                  (selectionChange)="onRequestsOfficeChange($event.value)"
                >
                  @for (office of offices; track office.code) {
                    <mat-option [value]="office.code">
                      {{ office.description || office.code }}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>

              <!-- Status Filter -->
              <mat-form-field
                appearance="outline"
                class="filter-field requests-filter-status-field"
              >
                <mat-label>Status</mat-label>
                <mat-select
                  [(value)]="requestsStatusFilter"
                  multiple
                  (selectionChange)="onRequestsStatusChange($event.value)"
                >
                  <mat-option value="PENDING">Pending</mat-option>
                  <mat-option value="APPROVED">Approved</mat-option>
                  <mat-option value="DENIED">Returned</mat-option>
                  <mat-option value="CANCELLED">Cancelled</mat-option>
                </mat-select>
              </mat-form-field>

              <!-- Time Frame Filter -->
              <mat-form-field
                appearance="outline"
                class="filter-field time-frame-field"
              >
                <mat-label>Time Frame</mat-label>
                <mat-select
                  [(value)]="requestsTimeFrameFilter"
                  multiple
                  (selectionChange)="onRequestsTimeFrameChange($event.value)"
                >
                  <mat-option value="PAST">Past</mat-option>
                  <mat-option value="CURRENT">Current</mat-option>
                  <mat-option value="FUTURE">Future</mat-option>
                </mat-select>
              </mat-form-field>

              <!-- Reset Button -->
              <button
                mat-stroked-button
                type="button"
                (click)="resetRequestsFilters()"
                class="reset-filters-btn"
                [disabled]="!hasRequestsFiltersChanged()"
              >
                <mat-icon>replay</mat-icon>
                Reset
              </button>
            </div>
          </mat-card-content>
        </mat-card>
      } @else {
        <mat-card class="filters-card">
          <mat-card-content>
            <form [formGroup]="filterForm" class="filters-form">
              <div class="filters-container">
                <!-- Main Search Row with Actions -->
                <div class="main-search-row">
                  <mat-form-field
                    appearance="outline"
                    class="filter-field search-field"
                  >
                    <mat-label>Search resources by name</mat-label>
                    <input
                      matInput
                      formControlName="primarySearch"
                      placeholder="Search resources by name"
                      autocomplete="off"
                      (keyup.enter)="onEnterKeySearch()"
                    />
                    <mat-icon
                      matSuffix
                      [style.cursor]="
                        filterForm.get('primarySearch')?.value
                          ? 'pointer'
                          : 'default'
                      "
                      (click)="
                        filterForm.get('primarySearch')?.value
                          ? filterForm.get('primarySearch')?.reset()
                          : null
                      "
                      >{{
                        filterForm.get("primarySearch")?.value
                          ? "close"
                          : "search"
                      }}</mat-icon
                    >
                  </mat-form-field>

                  <!-- Date Range Selector -->
                  <mat-form-field appearance="outline" class="date-range-field">
                    <mat-label>Billable Hours</mat-label>
                    <mat-select
                      [value]="selectedDateRange.key"
                      (selectionChange)="onDateRangeChange($event.value)"
                      [disabled]="isLoading"
                      panelClass="billable-hours-select-panel"
                    >
                      @for (option of dateRangeOptions; track option.key) {
                        <mat-option [value]="option.key">
                          {{ option.label }}
                        </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>

                  <!-- Capacity Filter -->
                  <mat-form-field
                    appearance="outline"
                    class="filter-field capacity-field"
                  >
                    <mat-label
                      >Capacity
                      {{
                        getSelectedCountDisplay("capacityStatuses")
                      }}</mat-label
                    >
                    <mat-select formControlName="capacityStatuses" multiple>
                      @for (
                        option of capacityFilterOptions;
                        track option.value
                      ) {
                        <mat-option [value]="option.value">
                          {{ option.label }}
                        </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>

                  <!-- Filter Actions -->
                  <div class="filter-actions">
                    <button
                      mat-stroked-button
                      class="advanced-search-btn"
                      type="button"
                      (click)="toggleAdvancedSearch()"
                      [class.active]="isAdvancedSearchButtonActive()"
                    >
                      <mat-icon>{{
                        isAdvancedSearchMode ? "search_off" : "manage_search"
                      }}</mat-icon>
                      {{ getAdvancedSearchButtonText() }}
                    </button>
                    <button
                      mat-stroked-button
                      class="reset-filters-btn"
                      type="button"
                      (click)="resetFilters()"
                      [disabled]="!hasFiltersChanged()"
                    >
                      <mat-icon>replay</mat-icon>
                      Reset
                    </button>
                  </div>
                </div>

                <!-- Advanced Search Fields (shown when in advanced mode) -->
                @if (isAdvancedSearchMode) {
                  <div class="advanced-search-section" @expandCollapse>
                    <!-- Advanced Search Row -->
                    <div class="advanced-search-row">
                      <!-- Titles Filter -->
                      <app-searchable-multi-select
                        class="filter-field titles-field"
                        label="Titles"
                        searchPlaceholder="Search titles..."
                        [options]="titles"
                        [formControl]="$any(filterForm.get('titles'))"
                      ></app-searchable-multi-select>

                      <!-- Class Year Filter -->
                      <app-searchable-multi-select
                        class="filter-field class-year-field"
                        label="Class Year"
                        searchPlaceholder="Search class years..."
                        [options]="classYearOptions"
                        emptyOptionsText="No class years available"
                        [formControl]="$any(filterForm.get('classYears'))"
                      ></app-searchable-multi-select>

                      <!-- Seniority Filter -->
                      <mat-form-field
                        appearance="outline"
                        class="filter-field seniority-field"
                      >
                        <mat-label
                          >Seniority
                          {{
                            getSelectedCountDisplay("seniorityCodes")
                          }}</mat-label
                        >
                        <mat-select formControlName="seniorityCodes" multiple>
                          @for (
                            option of seniorityFilterOptions;
                            track option.value
                          ) {
                            <mat-option [value]="option.value">
                              {{ option.label }}
                            </mat-option>
                          }
                        </mat-select>
                      </mat-form-field>

                      <!-- Office Filter -->
                      <mat-form-field
                        appearance="outline"
                        class="filter-field offices-field"
                      >
                        <mat-label
                          >Offices
                          {{ getSelectedCountDisplay("offices") }}</mat-label
                        >
                        <mat-select formControlName="offices" multiple>
                          @for (office of offices; track office.code) {
                            <mat-option [value]="office.code">
                              {{ office.description || office.code }}
                            </mat-option>
                          }
                        </mat-select>
                      </mat-form-field>

                      <!-- Practice Area Filter -->
                      <mat-form-field
                        appearance="outline"
                        class="filter-field practice-areas-field"
                      >
                        <mat-label
                          >Practice Areas
                          {{
                            getSelectedCountDisplay("practiceAreaCodes")
                          }}</mat-label
                        >
                        <mat-select
                          formControlName="practiceAreaCodes"
                          multiple
                        >
                          @for (pa of practiceAreas; track pa.practiceCode) {
                            <mat-option [value]="pa.practiceCode">
                              {{ pa.description }}
                            </mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                    </div>

                    <!-- Resource Profile Attribute Filters Row -->
                    <div class="advanced-search-row">
                      <!-- Skills Filter -->
                      <app-searchable-multi-select
                        class="filter-field attribute-field"
                        label="Skills"
                        searchPlaceholder="Search skills..."
                        emptyOptionsText="No skills have been added"
                        [options]="attributeFilterOptions['skillCodes']"
                        [formControl]="$any(filterForm.get('skillCodes'))"
                      ></app-searchable-multi-select>

                      <!-- Jurisdictions Filter -->
                      <app-searchable-multi-select
                        class="filter-field attribute-field"
                        label="Jurisdictions"
                        searchPlaceholder="Search jurisdictions..."
                        emptyOptionsText="No jurisdictions have been added"
                        [options]="attributeFilterOptions['jurisdictionCodes']"
                        [formControl]="
                          $any(filterForm.get('jurisdictionCodes'))
                        "
                      ></app-searchable-multi-select>

                      <!-- Languages Filter -->
                      <app-searchable-multi-select
                        class="filter-field attribute-field"
                        label="Languages"
                        searchPlaceholder="Search languages..."
                        emptyOptionsText="No languages have been added"
                        [options]="attributeFilterOptions['languageCodes']"
                        [formControl]="$any(filterForm.get('languageCodes'))"
                      ></app-searchable-multi-select>

                      <!-- Interests Filter -->
                      <app-searchable-multi-select
                        class="filter-field attribute-field"
                        label="Interests"
                        searchPlaceholder="Search interests..."
                        emptyOptionsText="No interests have been added"
                        [options]="attributeFilterOptions['interestCodes']"
                        [formControl]="$any(filterForm.get('interestCodes'))"
                      ></app-searchable-multi-select>

                      <!-- Sub-Practice Areas Filter -->
                      <app-searchable-multi-select
                        class="filter-field attribute-field"
                        label="Sub-Practice Areas"
                        searchPlaceholder="Search sub-practice areas..."
                        emptyOptionsText="No sub-practice areas have been added"
                        [options]="
                          attributeFilterOptions['subPracticeAreaCodes']
                        "
                        [formControl]="
                          $any(filterForm.get('subPracticeAreaCodes'))
                        "
                      ></app-searchable-multi-select>
                    </div>
                  </div>
                }
              </div>
            </form>
          </mat-card-content>
        </mat-card>
      }

      <!-- Calendar View -->
      @if (viewMode === "calendar") {
        <mat-card
          class="calendar-card"
          [class.calendar-loading]="isCalendarLoading"
        >
          <div class="calendar-status-filter">
            <mat-form-field appearance="outline" class="calendar-status-field">
              <mat-select
                [value]="calendarStatusFilter"
                multiple
                (selectionChange)="onCalendarStatusChange($event.value)"
              >
                <mat-option value="APPROVED">Approved</mat-option>
                <mat-option value="PENDING">Pending</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
          <!-- calendar-print-area marks the only subtree _print.scss keeps
               visible when printing; see calendar-print.utils.ts. -->
          <mat-card-content class="calendar-print-area">
            <div class="calendar-print-caption">
              <span class="calendar-print-caption__title"
                >Resources Directory — Time Off</span
              >
              <span class="calendar-print-caption__meta"
                >Showing {{ calendarStatusLabels }} · Printed
                {{ printedOn | date: "mediumDate" }}</span
              >
            </div>
            <full-calendar
              #timeOffCalendar
              [options]="calendarOptions"
            ></full-calendar>
          </mat-card-content>
        </mat-card>
      }

      <!-- Resources Table -->
      @if (viewMode === "table") {
        <mat-card class="table-card no-padding">
          <div class="resources-table-container">
            <!-- Loading State for Search Operations Only -->
            @if (isSearching) {
              <div class="loading-state">
                <mat-spinner diameter="32"></mat-spinner>
                <p>Searching resources...</p>
              </div>
            }

            <!-- Error State -->
            @if (error && !isLoading) {
              <app-error-state
                [message]="error"
                (retry)="retry()"
              ></app-error-state>
            }

            <!-- Empty State -->
            @if (!isLoading && !error && resources.length === 0) {
              <div class="empty-state">
                <mat-icon class="empty-icon">business</mat-icon>
                <h3>No resources found</h3>
                <p>Try adjusting your search criteria or filters.</p>
              </div>
            }

            <!-- Resources Table - Show during non-search loading (skeleton) or when data exists -->
            @if (
              !isSearching && !error && (isLoading || resources.length > 0)
            ) {
              <div class="table-scroll-wrapper">
                <table
                  mat-table
                  [dataSource]="dataSource"
                  matSort
                  #headerSort="matSort"
                  class="resources-table"
                >
                  <!-- Capacity Column -->
                  <ng-container matColumnDef="capacity">
                    <th
                      mat-header-cell
                      *matHeaderCellDef
                      class="capacity-header"
                    >
                      <div class="capacity-header-container">
                        <span>Capacity</span>
                        <mat-icon
                          class="capacity-info-icon"
                          (mouseenter)="showCapacityLegendHover($event)"
                          (mouseleave)="hideCapacityLegendHover()"
                        >
                          help
                        </mat-icon>
                      </div>
                    </th>
                    <td
                      mat-cell
                      *matCellDef="let resource"
                      class="capacity-cell"
                      (click)="$event.stopPropagation()"
                    >
                      @if (resource.id < 0) {
                        <div class="skeleton-dots">
                          <div class="skeleton-dot"></div>
                          <div class="skeleton-dot"></div>
                          <div class="skeleton-dot"></div>
                          <div class="skeleton-dot"></div>
                        </div>
                      } @else {
                        <div class="capacity-dots">
                          @for (
                            weeklyCapacity of getWeeklyCapacities(resource);
                            track $index
                          ) {
                            <svg
                              class="status-dot-svg"
                              [class.current-week]="$index === 1"
                              viewBox="0 0 8 8"
                              [matTooltip]="getCapacityTooltip(weeklyCapacity)"
                              matTooltipPosition="above"
                            >
                              <circle
                                cx="4"
                                cy="4"
                                [attr.r]="
                                  getCapacityStatusClass(
                                    weeklyCapacity.statusCode
                                  ) === 'status-dot-unknown'
                                    ? 3
                                    : 4
                                "
                                class="status-dot {{
                                  getCapacityStatusClass(
                                    weeklyCapacity.statusCode
                                  )
                                }}"
                              />
                            </svg>
                          }
                        </div>
                      }
                    </td>
                  </ng-container>

                  <!-- Expected Period Hours Column.
                       Sorts like every other column here: the list endpoint returns all matching
                       resources in one call, and MatTableDataSource does the sorting and paging in
                       the browser. The comparable value comes from the sortingDataAccessor, which
                       reuses the same current-period lookup this cell renders. -->
                  <ng-container matColumnDef="expectedPeriodHours">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header disableClear>
                      Expected Period Hours
                    </th>
                    <td mat-cell *matCellDef="let resource" class="hours-cell">
                      @if (resource.id < 0) {
                        <div class="skeleton-line medium"></div>
                      } @else {
                        <div class="hours-content">
                          {{
                            formatExpectedPeriodHours(
                              getCurrentPeriodExpectedHours(resource)
                            )
                          }}
                        </div>
                      }
                    </td>
                  </ng-container>

                  <!-- Billable Hours Column -->
                  <ng-container matColumnDef="billableHours">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header disableClear>
                      Billable Hours
                    </th>
                    <td mat-cell *matCellDef="let resource" class="hours-cell">
                      @if (resource.id < 0) {
                        <div class="skeleton-line medium"></div>
                      } @else {
                        <div class="hours-content">
                          {{ formatHours(resource.billableHours) }}
                        </div>
                      }
                    </td>
                  </ng-container>

                  <!-- Non-Billable Hours Column -->
                  <ng-container matColumnDef="nonBillableHours">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header disableClear>
                      Non-Billable Hours
                    </th>
                    <td mat-cell *matCellDef="let resource" class="hours-cell">
                      @if (resource.id < 0) {
                        <div class="skeleton-line medium"></div>
                      } @else {
                        <div class="hours-content">
                          {{ formatHours(resource.nonBillableHours) }}
                        </div>
                      }
                    </td>
                  </ng-container>

                  <!-- Pro Bono Hours Column -->
                  <ng-container matColumnDef="proBonoHours">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header disableClear>
                      Pro Bono Hours
                    </th>
                    <td mat-cell *matCellDef="let resource" class="hours-cell">
                      @if (resource.id < 0) {
                        <div class="skeleton-line medium"></div>
                      } @else {
                        <div class="hours-content">
                          {{ formatHours(resource.proBonoHours) }}
                        </div>
                      }
                    </td>
                  </ng-container>

                  <!-- Business Development Hours Column -->
                  <ng-container matColumnDef="businessDevelopmentHours">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header disableClear>
                      Business Dev Hours
                    </th>
                    <td mat-cell *matCellDef="let resource" class="hours-cell">
                      @if (resource.id < 0) {
                        <div class="skeleton-line medium"></div>
                      } @else {
                        <div class="hours-content">
                          {{ formatHours(resource.businessDevelopmentHours) }}
                        </div>
                      }
                    </td>
                  </ng-container>

                  <!-- Absence Hours Column -->
                  <ng-container matColumnDef="absenceHours">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header disableClear>
                      Absence Hours
                    </th>
                    <td mat-cell *matCellDef="let resource" class="hours-cell">
                      @if (resource.id < 0) {
                        <div class="skeleton-line medium"></div>
                      } @else {
                        <div class="hours-content">
                          {{ formatHours(resource.absenceHours) }}
                        </div>
                      }
                    </td>
                  </ng-container>

                  <!-- Firm Hours Column -->
                  <ng-container matColumnDef="firmHours">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header disableClear>
                      Firm Hours
                    </th>
                    <td mat-cell *matCellDef="let resource" class="hours-cell">
                      @if (resource.id < 0) {
                        <div class="skeleton-line medium"></div>
                      } @else {
                        <div class="hours-content">
                          {{ formatHours(resource.firmHours) }}
                        </div>
                      }
                    </td>
                  </ng-container>

                  <!-- Total Hours Column -->
                  <ng-container matColumnDef="totalHours">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header disableClear>
                      Total Hours
                    </th>
                    <td mat-cell *matCellDef="let resource" class="hours-cell">
                      @if (resource.id < 0) {
                        <div class="skeleton-line medium"></div>
                      } @else {
                        <div class="hours-content">
                          {{ formatHours(resource.totalHours) }}
                        </div>
                      }
                    </td>
                  </ng-container>

                  <!-- Full Name Column -->
                  <ng-container matColumnDef="fullName">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>
                      Name
                    </th>
                    <td mat-cell *matCellDef="let resource" class="name-cell">
                      @if (resource.id < 0) {
                        <!-- Skeleton content -->
                        <div class="resource-info skeleton-row">
                          <div class="skeleton-name-lines">
                            <div class="skeleton-line long"></div>
                          </div>
                        </div>
                      } @else {
                        <!-- Real content -->
                        <div class="resource-info">
                          <div class="resource-details">
                            <div class="resource-name">
                              <a
                                href=""
                                (click)="
                                  $event.preventDefault();
                                  navigateToResource(resource, $event)
                                "
                              >
                                <strong>{{ resource.fullName }}</strong>
                              </a>
                            </div>
                          </div>
                        </div>
                      }
                    </td>
                  </ng-container>

                  <!-- Title Column -->
                  <ng-container matColumnDef="title">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>
                      Title
                    </th>
                    <td mat-cell *matCellDef="let resource" class="title-cell">
                      @if (resource.id < 0) {
                        <div class="skeleton-line medium"></div>
                      } @else {
                        {{ resource.title }}
                      }
                    </td>
                  </ng-container>

                  <!-- Office Column -->
                  <ng-container matColumnDef="office">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>
                      Office
                    </th>
                    <td mat-cell *matCellDef="let resource" class="office-cell">
                      @if (resource.id < 0) {
                        <div class="skeleton-line medium"></div>
                      } @else {
                        {{ resource.office }}
                      }
                    </td>
                  </ng-container>

                  <!-- Class Year Column -->
                  <ng-container matColumnDef="classYear">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>
                      Class Year
                    </th>
                    <td
                      mat-cell
                      *matCellDef="let resource"
                      class="class-year-cell"
                    >
                      @if (resource.id < 0) {
                        <div class="skeleton-line short"></div>
                      } @else {
                        {{ resource.classYear }}
                      }
                    </td>
                  </ng-container>

                  <!-- Seniority Column -->
                  <ng-container matColumnDef="seniority">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>
                      Seniority
                    </th>
                    <td
                      mat-cell
                      *matCellDef="let resource"
                      class="seniority-cell"
                    >
                      @if (resource.id < 0) {
                        <div class="skeleton-line short"></div>
                      } @else {
                        {{ resource.seniority || "—" }}
                      }
                    </td>
                  </ng-container>

                  <!-- Practice Areas Column -->
                  <ng-container matColumnDef="practiceAreas">
                    <th mat-header-cell *matHeaderCellDef>Practice Areas</th>
                    <td mat-cell *matCellDef="let resource">
                      @if (resource.id < 0) {
                        <div class="skeleton-chips">
                          <div class="skeleton-chip"></div>
                        </div>
                      } @else if (
                        resource.practiceAreas &&
                        resource.practiceAreas.length > 0
                      ) {
                        <app-membership-chips
                          [items]="resource.practiceAreas"
                          labelKey="description"
                        />
                      } @else {
                        <span class="text-muted">—</span>
                      }
                    </td>
                  </ng-container>

                  <!-- Tag Columns (read-only pills, Attributes-card styling) -->
                  @for (tagColumn of tagColumns; track tagColumn.key) {
                    <ng-container [matColumnDef]="tagColumn.key">
                      <th mat-header-cell *matHeaderCellDef>
                        {{ getColumnLabel(tagColumn.key) }}
                      </th>
                      <td mat-cell *matCellDef="let resource">
                        @let tags =
                          getResourceTags(resource, tagColumn.category);
                        @if (resource.id < 0) {
                          <div class="skeleton-chips">
                            <div class="skeleton-chip"></div>
                          </div>
                        } @else if (tags.length > 0) {
                          <app-attribute-tag-chips [tags]="tags" />
                        } @else {
                          <span class="text-muted">&mdash;</span>
                        }
                      </td>
                    </ng-container>
                  }

                  <!-- Actions Column -->
                  <ng-container matColumnDef="openInDialog" stickyEnd>
                    <th mat-header-cell *matHeaderCellDef></th>
                    <td
                      mat-cell
                      *matCellDef="let resource"
                      class="actions-cell"
                      (click)="$event.stopPropagation()"
                    >
                      @if (resource.employeeId) {
                        <div class="actions-cell-inner">
                          <button
                            mat-icon-button
                            class="action-btn"
                            matTooltip="View resource details"
                            (click)="navigateToResource(resource, $event)"
                          >
                            <mat-icon>visibility</mat-icon>
                          </button>
                          <button
                            mat-icon-button
                            class="action-btn"
                            matTooltip="Open in floating window"
                            (click)="openInFloatingDialog(resource, $event)"
                          >
                            <mat-icon>select_window_2</mat-icon>
                          </button>
                        </div>
                      }
                    </td>
                  </ng-container>

                  <tr
                    mat-header-row
                    *matHeaderRowDef="displayedColumns; sticky: true"
                  ></tr>
                  <tr
                    mat-row
                    *matRowDef="let _row; columns: displayedColumns"
                  ></tr>
                </table>
              </div>

              <!-- Capacity Legend Hover Card -->
              @if (showCapacityLegend) {
                <div
                  class="capacity-legend-hover-card"
                  [style.left.px]="capacityLegendPosition.x"
                  [style.top.px]="capacityLegendPosition.y"
                >
                  <mat-card class="hover-card-content">
                    <mat-card-content>
                      <app-capacity-legend></app-capacity-legend>
                    </mat-card-content>
                  </mat-card>
                </div>
              }

              <!-- Paginator -->
              @if (resources.length > 0) {
                <mat-paginator
                  #resourcesPaginator
                  [length]="totalCount"
                  [pageSize]="pageSize"
                  [pageIndex]="pageIndex"
                  [pageSizeOptions]="[10, 25, 50, 100]"
                  (page)="onPageChange($event)"
                  showFirstLastButtons
                  class="resources-paginator"
                >
                </mat-paginator>
              }
            }
          </div>
        </mat-card>
      }

      <!-- Time Off Requests Table -->
      @if (viewMode === "requests") {
        <mat-card class="requests-card no-padding">
          <div class="requests-table-container">
            @if (isRequestsLoading) {
              <div class="loading-state">
                <mat-spinner diameter="32"></mat-spinner>
                <p>Loading requests...</p>
              </div>
            } @else if (timeOffRequests.length === 0) {
              <div class="empty-state">
                <mat-icon class="empty-icon">business</mat-icon>
                <h3>No time off requests</h3>
                <p>There are no requests matching the selected filters.</p>
              </div>
            } @else {
              <div class="table-wrapper">
                <table
                  mat-table
                  [dataSource]="requestsDataSource"
                  matSort
                  #requestsHeaderSort="matSort"
                  matSortActive="startDate"
                  matSortDirection="asc"
                  class="requests-table"
                >
                  <!-- Name Column -->
                  <ng-container matColumnDef="fullName">
                    <th
                      mat-header-cell
                      *matHeaderCellDef
                      mat-sort-header="fullName"
                    >
                      Resource
                    </th>
                    <td mat-cell *matCellDef="let entry">
                      <span
                        class="resource-link"
                        (click)="navigateToResourceById(entry.employeeId)"
                      >
                        {{ entry.fullName }}
                      </span>
                    </td>
                  </ng-container>

                  <!-- Type Column -->
                  <ng-container matColumnDef="typeName">
                    <th
                      mat-header-cell
                      *matHeaderCellDef
                      mat-sort-header="typeName"
                    >
                      Type
                    </th>
                    <td mat-cell *matCellDef="let entry">
                      {{ entry.typeName }}
                    </td>
                  </ng-container>

                  <!-- Start Date Column -->
                  <ng-container matColumnDef="startDate">
                    <th
                      mat-header-cell
                      *matHeaderCellDef
                      mat-sort-header="startDate"
                    >
                      Start
                    </th>
                    <td mat-cell *matCellDef="let entry">
                      {{ entry.startDate | date: "MM/dd/yyyy" }}
                    </td>
                  </ng-container>

                  <!-- End Date Column -->
                  <ng-container matColumnDef="endDate">
                    <th
                      mat-header-cell
                      *matHeaderCellDef
                      mat-sort-header="endDate"
                    >
                      End
                    </th>
                    <td mat-cell *matCellDef="let entry">
                      {{ entry.endDate | date: "MM/dd/yyyy" }}
                    </td>
                  </ng-container>

                  <!-- Days Off Column -->
                  <ng-container matColumnDef="daysOff">
                    <th
                      mat-header-cell
                      *matHeaderCellDef
                      mat-sort-header="daysOff"
                      class="days-off-header"
                    >
                      Days Off
                    </th>
                    <td mat-cell *matCellDef="let entry">
                      {{ entry.daysOff }}
                    </td>
                  </ng-container>

                  <!-- Office Column -->
                  <ng-container matColumnDef="officeName">
                    <th
                      mat-header-cell
                      *matHeaderCellDef
                      mat-sort-header="officeName"
                    >
                      Office
                    </th>
                    <td mat-cell *matCellDef="let entry">
                      {{ entry.officeName || "—" }}
                    </td>
                  </ng-container>

                  <!-- Practice Areas Column -->
                  <ng-container matColumnDef="practiceAreas">
                    <th mat-header-cell *matHeaderCellDef>Practice Areas</th>
                    <td mat-cell *matCellDef="let entry">
                      @if (
                        entry.practiceAreas && entry.practiceAreas.length > 0
                      ) {
                        <app-membership-chips
                          [items]="entry.practiceAreas"
                          labelKey="description"
                        />
                      } @else {
                        <span class="text-muted">—</span>
                      }
                    </td>
                  </ng-container>

                  <!-- Status Column -->
                  <ng-container matColumnDef="statusName">
                    <th
                      mat-header-cell
                      *matHeaderCellDef
                      mat-sort-header="statusName"
                      class="status-header"
                    >
                      Status
                    </th>
                    <td mat-cell *matCellDef="let entry">
                      <span
                        class="badge"
                        [class.badge-pending]="entry.statusCode === 'PENDING'"
                        [class.badge-approved]="entry.statusCode === 'APPROVED'"
                        [class.badge-denied]="entry.statusCode === 'DENIED'"
                        [class.badge-cancelled]="
                          entry.statusCode === 'CANCELLED'
                        "
                      >
                        {{ entry.statusName }}
                      </span>
                    </td>
                  </ng-container>

                  <!-- Actions Column -->
                  <ng-container matColumnDef="actions">
                    <th mat-header-cell *matHeaderCellDef></th>
                    <td mat-cell *matCellDef="let entry">
                      <div class="actions-cell-inner">
                        <button
                          mat-icon-button
                          disableRipple
                          class="details-icon-button"
                          matTooltip="View details"
                          (click)="
                            openRequestDetailsDialog(entry);
                            $event.stopPropagation()
                          "
                        >
                          <mat-icon>more_vert</mat-icon>
                        </button>
                      </div>
                    </td>
                  </ng-container>

                  <tr
                    mat-header-row
                    *matHeaderRowDef="requestsDisplayedColumns; sticky: true"
                  ></tr>
                  <tr
                    mat-row
                    *matRowDef="let _row; columns: requestsDisplayedColumns"
                  ></tr>
                </table>
              </div>

              <!-- Paginator -->
              <mat-paginator
                #requestsPaginator
                [pageSizeOptions]="[10, 25, 50]"
                showFirstLastButtons
                class="requests-paginator"
              >
              </mat-paginator>
            }
          </div>
        </mat-card>
      }
    </div>
  </main>
</div>
